import { FC } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PermissionGatedWrapper } from '../../../../../components/Fallback/PermissionGatedWrapper';
import { IFundingDataModel } from '../../../../../data-models/company-fundings.data-model';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { CompanyFinancingHistoryState } from '../../../state/CompanyFundingsState';
import {
  isDrawerOpenState,
  selectedActivityTypeState,
  selectedCompanyIdProfileAtom,
  selectedDealRoundAtom,
} from '../../../state/UIStateJ';
import { companyStateJ } from '../../../../../services/state/CompanyStateJ';
import { isPortfolioCompany } from '../../../../../data-models/company.data-model';
import {
  StyledTable,
  StyledTableCell,
  StyledTableHeaderRow,
  StyledTableRow,
} from '../../../components/Table/StyledGridComponents';
import { ListWithMoreChip } from '../../../Companies/CompanyCard/ListWithMoreChip';
import { RoundNameRenderer } from '../../../../../components/AgTable/cell-renderers/RoundRenderer';
import { headlineCurrencyFormatterId } from '../../../../../client-customizations/default/formatters';
import { FMT } from '../../../../../util/formatter-service';
import { SecondaryForbiddenMessage } from '../../../../CompanyProfiles2/Summary/constants/forbidden-messages.constants';
import { NoDataMessage2 } from '../../../components/Messages/HelpMessage';

import { MaggieFeatureFlags } from '../../../../../util/feature-flags';

interface ColumnConfig {
  name: string;
  type: 'numeric' | 'text';
  portcoOnly?: true;
  minWidth?: string;
}

const allColumns: readonly ColumnConfig[] = [
  { name: 'Stage', type: 'text', minWidth: '8rem' }, // fundingRoundCategory
  { name: 'Round', type: 'text', minWidth: '5rem' }, // fundingRound
  { name: 'Round Date', type: 'numeric', minWidth: '6rem' }, // dealDate
  // Wider minWidths than the other numeric columns: headers are `noWrap`, so these
  // labels would ellipsize at 7rem (FD-4129).
  { name: 'My Round Investment', type: 'numeric', portcoOnly: true, minWidth: '8rem' }, // investmentAmount
  { name: 'My Cumulative Total Investment', type: 'numeric', portcoOnly: true, minWidth: '12rem' }, // cumulativeInvestment
  { name: 'Financing Amount Raised', type: 'numeric', minWidth: '7rem' }, // dealSizeInMillions
  { name: 'Post-Money Valuation', type: 'numeric', minWidth: '7rem' }, // valuationInMillions
  { name: 'Lead Investor(s)', type: 'text', minWidth: '8rem' }, // leadInvestors
  { name: 'Investor(s)', type: 'text', minWidth: '8rem' }, // investors
];

export const getColumns = (isPortfolio: boolean) =>
  allColumns.filter((col) => isPortfolio || !col.portcoOnly);

const getGridTemplateColumns = (cols: readonly ColumnConfig[]) =>
  cols.map((col) => `minmax(${col.minWidth ?? '0'}, 1fr)`).join(' ');

export const FinancingHistoryTable: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const isPortfolio = isPortfolioCompany(company!);
  const { showDealDocsInActivityDrawer } = useFlags<MaggieFeatureFlags>();
  const fundings = useAtomValue(
    CompanyFinancingHistoryState({
      companyId,
      isPortfolioCompany: isPortfolio,
    })
  );
  const columns = getColumns(isPortfolio);

  // State setters for opening drawer and selecting round
  const setIsDrawerOpen = useSetAtom(isDrawerOpenState);
  const setSelectedActivityType = useSetAtom(selectedActivityTypeState);
  const setSelectedRound = useSetAtom(selectedDealRoundAtom);

  // Only enable row click for portfolio companies with the feature flag enabled
  const isDealDocsClickEnabled = isPortfolio && showDealDocsInActivityDrawer;

  const handleRowClick = (roundName: string, dealDate: string) => {
    // Update states in sequence - React 18 will batch these updates.
    // The selected round highlights the matching card in the Deal Docs sidebar.
    setSelectedActivityType('Deal Docs');
    // Increment version to track each click, enabling re-expansion of manually collapsed rounds
    setSelectedRound((prev) => ({
      name: roundName,
      date: dealDate,
      selectionVersion:
        prev?.name === roundName && prev?.date === dealDate ? (prev.selectionVersion ?? 0) + 1 : 1,
    }));
    setIsDrawerOpen(true);
  };

  if (fundings.length === 0) {
    return (
      <PermissionGatedWrapper
        permissions={[PermissionKey.canViewCompanyFinancials]}
        fallbackMessage={SecondaryForbiddenMessage}
      >
        <NoDataMessage2 message='No financing history available' />
      </PermissionGatedWrapper>
    );
  }

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewCompanyFinancials]}
      fallbackMessage={SecondaryForbiddenMessage}
    >
      <div style={{ contain: 'inline-size', overflowX: 'auto', marginTop: '0.5rem', marginBottom: '1rem' }}>
        <StyledTable
          style={{
            width: 'max-content',
            minWidth: '100%',
            gridTemplateColumns: getGridTemplateColumns(columns),
          }}
        >
          <StyledTableHeaderRow>
            {columns.map((col, i) => (
              <StyledTableCell
                key={col.name}
                title={col.name}
                style={
                  i === 0
                    ? { paddingLeft: '0.75rem' }
                    : i === columns.length - 1
                      ? { paddingRight: '0.75rem' }
                      : undefined
                }
                type={col.type}
                header
              >
                {col.name}
              </StyledTableCell>
            ))}
          </StyledTableHeaderRow>
          {fundings.map((data) => (
            <StyledTableRow
              key={`${data.fundingRound}-${data.dealDate}`}
              style={{ padding: '0 0.5rem', cursor: isDealDocsClickEnabled ? 'pointer' : 'default' }}
              onClick={
                isDealDocsClickEnabled ? () => handleRowClick(data.fundingRound, data.dealDate) : undefined
              }
            >
              <RoundNameRenderer
                name={data.fundingRoundCategory}
                style={{ fontSize: '0.85rem', margin: '0' }}
              />
              <StyledTableCell type='text'>{data.fundingRound ?? ''}</StyledTableCell>
              <StyledTableCell type='numeric'>
                {data.dealDate ? FMT.format('date', data.dealDate) : ''}
              </StyledTableCell>
              {isPortfolio && (
                <StyledTableCell type='numeric'>
                  {data.investmentAmount
                    ? FMT.format(headlineCurrencyFormatterId, data.investmentAmount)
                    : ''}
                </StyledTableCell>
              )}
              {isPortfolio && (
                <StyledTableCell type='numeric'>
                  {data.cumulativeInvestment
                    ? FMT.format(headlineCurrencyFormatterId, data.cumulativeInvestment)
                    : ''}
                </StyledTableCell>
              )}
              <StyledTableCell type='numeric'>
                {data.dealSizeInMillions
                  ? FMT.format(headlineCurrencyFormatterId, data.dealSizeInMillions * 1000000)
                  : ''}
              </StyledTableCell>
              <StyledTableCell type='numeric'>
                {data.valuationInMillions
                  ? FMT.format(headlineCurrencyFormatterId, data.valuationInMillions * 1_000_000)
                  : ''}
              </StyledTableCell>

              <StyledTableCell>
                <ListWithMoreChip
                  title={'Lead Investor(s)'}
                  items={data.leadInvestors?.filter(Boolean) ?? []}
                />
              </StyledTableCell>
              {data.investors?.length ? (
                <StyledTableCell>
                  <ListWithMoreChip
                    items={data.investors?.map((investor) => investor.orgName) ?? []}
                    title={'Investors'}
                  />
                </StyledTableCell>
              ) : (
                <StyledTableCell />
              )}
            </StyledTableRow>
          ))}
        </StyledTable>
      </div>
    </PermissionGatedWrapper>
  );
};

export function fundsByDateDescending(funding1: IFundingDataModel, funding2: IFundingDataModel) {
  const dateDiff = new Date(funding2.dealDate).getTime() - new Date(funding1.dealDate).getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }
  // Same Round Date: order by "My Cumulative Total Investment" descending so the
  // running-total column reads consistently top-down (FD-2842).
  return (funding2.cumulativeInvestment ?? 0) - (funding1.cumulativeInvestment ?? 0);
}
